// Sensitive lead post (ADR 0021).
//
// Companion to funnel-ping.ts, fired once at the Step 1 (lead capture)
// submit. Unlike the funnel ping — which is deliberately PII-free — this
// payload carries the lead's name/email/phone to the middleware's
// write-only /leads endpoint, which stores it in the KMS-encrypted lead
// vault for client-requested lead-recovery marketing. The middleware is
// the ONLY consumer; nothing here goes to GA4/sGTM.
//
// Fire-and-forget by design: failures must never block the booking flow
// or surface in the TRAFFIC drawer, so we skip apiFetch and swallow all
// errors, exactly like the funnel ping.

import { getAttribution } from './attribution';
import { getWebSubmissionId } from './dispatch';
import { resolveApiConfigOrNull } from '../api/client';

interface LeadFields {
  name: string;
  email: string;
  phone?: string;
  store?: string;
  fitting_selection?: string;
}

export function postLead(fields: LeadFields): void {
  if (typeof window === 'undefined') return;
  // Resolve the base URL + bearer the SAME way the booking path does — host
  // config first, then the build-time default. Reading cfg.apiBaseUrl directly
  // silently no-op'd on host pages that inject only analytics config.
  const resolved = resolveApiConfigOrNull();
  if (!resolved) return;
  const { apiBaseUrl, bearerToken } = resolved;
  const cfg = window.CC_WIDGET_CONFIG ?? {};
  if (!fields.name || !fields.email) return;

  const attr = getAttribution();
  const body: Record<string, unknown> = {
    submission_id: getWebSubmissionId(),
    name: fields.name,
    email: fields.email,
    phone: fields.phone || undefined,
    store: fields.store,
    fitting_selection: fields.fitting_selection,
    embed_source: cfg.embedSource,
    utm_source: attr.utm_source,
    utm_medium: attr.utm_medium,
    utm_campaign: attr.utm_campaign,
    utm_content: attr.utm_content,
    utm_term: attr.utm_term,
    gclid: attr.gclid,
    landing_url: typeof location !== 'undefined' ? location.href : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
  };
  for (const k of Object.keys(body)) {
    if (body[k] === undefined) delete body[k];
  }

  const url = `${apiBaseUrl.replace(/\/$/, '')}/leads`;
  try {
    void fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
      },
    });
  } catch {
    // Swallow — the lead log never blocks the booking flow.
  }
}

/**
 * Seed the Salesforce Account for a Step 1 lead.
 *
 * Companion to postLead() and fired at the same moment. Where the lead vault
 * keeps a private record for CC to work from, this puts the customer into
 * Salesforce itself — so the people who identified themselves and never booked
 * are visible in the system CC already runs on, rather than only in an export.
 *
 * The middleware endpoint is idempotent on email: a repeat submission returns
 * the existing account instead of creating a second one. That is what lets this
 * fire on every Step 1 without tracking who has already been sent.
 *
 * Requires a studio. Salesforce rejects the call without one — it sets the new
 * account's owning store — so a submission with no studio selected is skipped
 * rather than sent to be refused. At Step 1 the studio has been chosen, so this
 * is a guard, not the normal path.
 *
 * Fire-and-forget, like everything else in this module. The booking flow must
 * not notice whether Salesforce answered.
 */
export function seedAccount(fields: {
  firstName: string;
  lastName: string;
  email: string;
  store?: string;
  phone?: string;
  /**
   * Marketing email consent from the checkbox on this step. Sent as a field
   * now that CC has one — it used to be recorded only as a sentence appended
   * to the appointment's free-text notes, which is not queryable and so not
   * usable as a consent record.
   */
  emailOptIn?: boolean;
  /**
   * SMS consent. Always sent, including false — Salesforce reads an ABSENT
   * smsOptIn on /Account as true, so leaving it out opts the customer in
   * without asking. Measured against the QA org 2026-09-02.
   */
  smsOptIn?: boolean;
  /**
   * The marketing snapshot's `submissionId` — a UUID minted at widget mount and
   * held in sessionStorage.
   *
   * It keys the Marketing Cloud funnel row. The SAME value travels on the
   * booking payload, which is what lets the confirmation update the row this
   * call creates rather than inserting beside it — so "started but never
   * completed" is answerable. Without it the middleware skips the Marketing
   * Cloud write entirely, because a row keyed on an empty string would merge
   * unrelated customers together.
   */
  sessionId?: string;
  /**
   * The widget's country code. The middleware reports only US traffic to
   * Marketing Cloud for now, and gates on this — so it must travel on BOTH this
   * call and the booking, or a session starts in one market and completes in
   * another as far as the report is concerned.
   */
  market?: string;
}): void {
  if (typeof window === 'undefined') return;
  // Resolve the base URL + bearer the SAME way the booking path does — host
  // config first, then the build-time default. Reading cfg.apiBaseUrl directly
  // silently no-op'd the account seed on host pages that inject only analytics
  // config (every international site), dropping the international Step 1 funnel.
  const resolved = resolveApiConfigOrNull();
  if (!resolved) return;
  const { apiBaseUrl, bearerToken } = resolved;
  if (!fields.email || !fields.firstName || !fields.lastName || !fields.store) return;

  const url = `${apiBaseUrl.replace(/\/$/, '')}/leads/account`;
  try {
    void fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        email: fields.email,
        firstName: fields.firstName,
        lastName: fields.lastName,
        storeName: fields.store,
        ...(fields.phone ? { phone: fields.phone } : {}),
        // Boolean: `undefined` means unstated, false means declined.
        ...(fields.emailOptIn === undefined ? {} : { emailOptIn: fields.emailOptIn }),
        // Never omitted — see the field comment above.
        smsOptIn: fields.smsOptIn ?? false,
        ...(fields.sessionId ? { sessionId: fields.sessionId } : {}),
        ...(fields.market ? { market: fields.market } : {}),
      }),
      keepalive: true,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
      },
    });
  } catch {
    // Swallow — account seeding never blocks the booking flow.
  }
}
